import { ref } from "vue"
import { defineStore, storeToRefs } from "pinia"
import type { Answers, Answer } from "@/features/drive/store/drive.types"
import {
  initAnswers,
  initExtra,
  factoryPatch,
  factoryReset,
} from "@/features/drive/store/drive.data"
import { useToast } from "vue-toastification"
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import {
  getAssessment,
  patchAssessment,
  postAssessment,
  deleteAssessment,
} from "@/api/viktor/assessment/assessment"
import { getVersionFromLang } from "@/features/drive/helpers/getVersionFromLang"
import { useQuestionsStore } from "@/features/drive/store/questions.store"
import { getDeviceType } from "@/helpers/getDeviceType"
import { driveActor } from "@/features/drive/machines/driveMachine"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { sleep } from "@/lib/utils"
import Bugsnag from "@bugsnag/js"

export const useAnswersStore = defineStore("driveAnswersStore", () => {
  const { goNext, setCurrentIndex } = useQuestionsStore()
  const i18nStore = useI18nStore()
  const { updateLocale, getLocaleIdFromIsoCode } = i18nStore
  const { currentLocale } = storeToRefs(useI18nStore())
  const { isFromInt } = storeToRefs(useUserStore())

  const answers = ref<Answers>(initAnswers)
  const isPatchExtraLoading = ref(false)

  const userStore = useUserStore()

  const toast = useToast()

  // GETTERS

  async function fetchDrive() {
    const driveAssessment = userStore.assessmentSummary.assessments.drive

    if (
      driveAssessment?.status === "finish" &&
      !driveAssessment?.can_restart &&
      !driveAssessment?.retake_test &&
      !driveAssessment.not_passed
    ) {
      // si le test est terminé, on sort de l'assessment
      if (isFromInt.value) answers.value.status = "thank-you"
      else window.location.reload()
    } else if (driveAssessment?.status === null) {
      // si le test n'a jamais été commencé on l'initalise
      await initDrive(false, null)
    } else if (
      driveAssessment?.status == "finish" &&
      (driveAssessment?.can_restart || driveAssessment?.retake_test)
    ) {
      // si le status est finish et que le test date de plus d'un an
      // delete + init new test
      await deleteAssessment("drive")
      await initDrive(false, null)
    } else if (
      driveAssessment?.status === "todo" ||
      driveAssessment?.status === "inprogress" ||
      !!driveAssessment?.not_passed
    ) {
      // autrement c'est que le test est en cours (inprogress)
      // donc on récupère les données du test et on popule les stores
      const assessment = await getAssessment("drive")
      answers.value = assessment
      if (Object.keys(answers.value?.data || {}).length === 0) {
        setCurrentIndex(0)
        answers.value.data.extra = initAnswers.data.extra
      } else {
        setCurrentIndex(answers.value.data?.items?.length)
      }

      // sometimes version was not set in the assessment
      let versionFull
      if (answers.value?.version === null) {
        versionFull = getVersionFromLang(currentLocale.value.iso_code)
        answers.value.version = versionFull
      } else {
        versionFull = answers.value.version
      }

      const lastUnderscoreIndex = versionFull.lastIndexOf("_")
      const langCode = versionFull.substring(0, lastUnderscoreIndex)
      userStore.audio = answers.value.data?.extra?.general_audio ? true : false
      answers.value.data.extra.elapsed_time = assessment.elapsed_time
      answers.value.data.extra.finished = assessment.finished_at ? true : false
      answers.value.data.extra.native_language = assessment.native_language
      answers.value.data.extra.wholesome_conditions =
        assessment.wholesome_conditions

      if (
        langCode !== currentLocale.value.iso_code &&
        answers.value?.status === "inprogress"
      ) {
        const locale_id = getLocaleIdFromIsoCode(langCode)
        await updateLocale(locale_id)
        // @TODO: set trad key from luciano
        toast.info("Language change")
      }
    }

    return answers.value
  }

  function getElapsedTime() {
    let time = 0
    answers.value.data.items.map((item) => (time += item.time_initial))
    return Math.round(time)
  }

  // ACTIONS

  async function initDrive(restarting?: boolean, lang?: number | null) {
    const payload: Answers = {
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: lang ? lang : currentLocale.value.id,
      status: "todo",
      data: { items: [], extra: { ...initExtra, isRestarting: restarting } },
    }
    const data = await postAssessment("drive", payload)
    answers.value = payload
    setCurrentIndex(0)

    return data
  }

  async function submitAnswer(answer: string, time: number) {
    if (
      !answers.value.data ||
      (Array.isArray(answers.value.data) && answers.value.data.length === 0)
    ) {
      answers.value.data = { items: [], extra: initExtra }
    }

    // split answer string by _ to get the question id
    const questionId = answer.split("_PROPOSAL_")[0]

    // check if the answer has been already answered from answer param starts with
    const alreadyAnswered = answers.value.data.items.find((a) =>
      a.answer.includes(questionId)
    )

    if (!alreadyAnswered) {
      const item: Answer = {
        answer: answer,
        audio: userStore.audio,
        device_type: getDeviceType(),
        screen_width: screen.width,
        time_initial: time,
      }
      answers.value.data.items.push(item)

      answers.value.data.extra.general_audio = userStore.audio

      const payload: Answers = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        locale_id: currentLocale.value.id,
        data: answers.value.data,
      }
      await patchAssessment("drive", payload)
    }
    await goNext()
  }

  async function patchExtra(type: string, answer: boolean) {
    isPatchExtraLoading.value = true
    try {
      answers.value.data.extra[type] = answer
      answers.value.data.extra.elapsed_time = getElapsedTime()

      const payload: Answers = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        locale_id: currentLocale.value.id,
        data: answers.value.data,
      }
      await patchAssessment("drive", payload)
    } finally {
      await sleep(300) // Timeout to prevent flicker
      isPatchExtraLoading.value = false
    }
  }

  async function patchRestart(
    type: string,
    answer: boolean,
    lang?: number | null
  ) {
    // try to submit final results
    try {
      await submitFinalResults(lang)
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.context = "DriveAnswersStore.patchRestart"
        event.severity = "error"
        event.unhandled = true
        event.addMetadata("request", {
          lang: lang,
          type: type,
          answer: answer,
        })
      })
    }

    // then, if user wants to restart,
    if (answer) {
      // delete active drive
      await deleteAssessment("drive")

      // set param isRestarting
      answers.value.data.extra.isRestarting = answer

      // init a brand new assessment with isRestarting param = true
      // isRestarting?: boolean, lang?: number | null
      await initDrive(true, lang ? lang : null)
      lang && (await updateLocale(lang))
      await userStore.fetchAssessmentSummary()

      // finally, send user to the right state (now or later)
      if (type === "now") {
        return driveActor.send({ type: "now" })
      } else {
        return driveActor.send({ type: "later" })
      }
    } else {
      return driveActor.send({ type: "done" })
    }
  }

  async function submitFinalResults(lang?: number | null) {
    try {
      answers.value.data.extra.elapsed_time = getElapsedTime()
      answers.value.data.extra.finished = true

      // if array length more than 90, we remove the last items [QA-7694]
      answers.value.data.items.length > 90 &&
        answers.value.data.items.splice(90)

      const payload: Answers = {
        version: getVersionFromLang(currentLocale.value.iso_code),
        locale_id: lang || currentLocale.value.id,
        data: answers.value.data,
      }

      // ultime patch to finish the test
      await patchAssessment("drive", payload)

      MixpanelInstance.track("drive_finish", {
        payload: payload,
      })

      return true
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.context = "DriveAnswersStore.submitFinalResults"
        event.severity = "error"
        event.unhandled = true
        event.addMetadata("request", {
          lang: lang,
          full_payload: {
            version: getVersionFromLang(currentLocale.value.iso_code),
            locale_id: lang || currentLocale.value.id,
            data: answers.value.data,
          },
        })
      })
      return false
    }
  }

  async function patchFactoryTest(isRestarting: boolean) {
    const payload = factoryPatch(isRestarting ? isRestarting : undefined) // Leave undefined as default
    await patchAssessment("drive", payload)
    window.location.reload()
  }

  async function resetUserDataLight() {
    await patchAssessment("drive", factoryReset)
    window.location.reload()
  }

  return {
    answers,
    fetchDrive,
    submitAnswer,
    patchExtra,
    isPatchExtraLoading,
    patchRestart,
    submitFinalResults,
    patchFactoryTest,
    resetUserDataLight,
  }
})
